import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Search, ArrowRight, Rows3, LayoutGrid } from 'lucide-react'
import { fetchPublicScams, type Scam } from '@/lib/api'
import { demoteMilitary, isMilitaryScam, orderForLaterLife } from '@/lib/later-life-scams'
import { resolveSite } from '@/lib/site'
import { useLocale } from '@/lib/locale'
import { useUi } from '@/lib/i18n/ui'
import { listQatarScams } from '@/data/qatar-scams'
import { scamIcon } from '@/lib/scam-icons'
import ScamCardSide from '@/components/ScamCard'

type LayoutMode = 'side' | 'top'
const LAYOUT_STORAGE_KEY = 'sd_scam_library_layout'

function readLayoutPref(): LayoutMode {
  if (typeof window === 'undefined') return 'side'
  try {
    const stored = window.localStorage.getItem(LAYOUT_STORAGE_KEY)
    if (stored === 'side' || stored === 'top') return stored
  } catch {
    // ignore
  }
  return 'side'
}

function writeLayoutPref(mode: LayoutMode) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(LAYOUT_STORAGE_KEY, mode)
  } catch {
    // ignore
  }
}

export const Route = createFileRoute('/scam-library/')({
  component: ScamLibraryIndex,
})

function ScamLibraryIndex() {
  const [q, setQ] = useState('')
  const [category, setCategory] = useState<string | null>(null)
  const [layout, setLayout] = useState<LayoutMode>('side')
  const t = useUi()
  const locale = useLocale()
  // The Qatar face ships its own bilingual library (the API set is US content).
  const isQatar = resolveSite() === 'qatar'

  // Hydrate the layout preference from localStorage after mount so the SSR /
  // first paint isn't a flash of the wrong layout (relevant later if/when we
  // add SSR; harmless today on a pure SPA).
  useEffect(() => {
    setLayout(readLayoutPref())
  }, [])

  const setLayoutPref = (mode: LayoutMode) => {
    setLayout(mode)
    writeLayoutPref(mode)
  }

  const remote = useQuery({
    queryKey: ['public-scams', 'US'],
    queryFn: () => fetchPublicScams({ market: 'US' }),
    retry: 0,
    enabled: !isQatar,
  })
  const data = isQatar ? listQatarScams(locale) : remote.data
  const isLoading = !isQatar && remote.isLoading
  const isError = !isQatar && remote.isError

  // On the military builds (dcuc / amba), sort the military & veteran entries
  // to the top of the grid. Everywhere else they sink to the back: the
  // GranGuard and NASAA faces lead with the later-life curation (NASAA's
  // audience is senior-fraud focused), and the umbrella keeps the API's
  // default (most-recent) order otherwise.
  const ordered = useMemo(() => {
    const list = data ?? []
    const site = resolveSite()
    if (site === 'qatar') return list
    if (site === 'dcuc' || site === 'amba') {
      return [...list.filter(isMilitaryScam), ...list.filter(s => !isMilitaryScam(s))]
    }
    if (site === 'granguard' || site === 'nasaa') return orderForLaterLife(list)
    return demoteMilitary(list)
  }, [data])

  // Category chips (Qatar face only: its set is large enough to need them and
  // its categories are curated; the API faces keep the plain search).
  const categories = useMemo(() => {
    if (!isQatar) return []
    const seen = new Map<string, string>()
    for (const s of ordered) if (s.scamCategory) seen.set(s.scamCategory.id, s.scamCategory.name)
    return [...seen.entries()].map(([id, name]) => ({ id, name }))
  }, [ordered, isQatar])

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const inCategory = category ? ordered.filter(s => s.scamCategory?.id === category) : ordered
    if (!needle) return inCategory
    return inCategory.filter(s =>
      `${s.title} ${s.description ?? ''} ${s.scamCategory?.name ?? ''}`
        .toLowerCase()
        .includes(needle),
    )
  }, [ordered, q, category])

  return (
    <div className="mx-auto max-w-7xl px-6 pb-20 pt-12">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
            {t.libraryTitle}
          </h1>
          <p className="mt-3 max-w-2xl text-lg leading-relaxed text-slate-500">
            {t.libraryIntro}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="relative block w-full sm:w-64">
            <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder={t.librarySearch}
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 ps-9 pe-3 text-base text-slate-700 placeholder:text-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
            />
          </label>
          <LayoutToggle mode={layout} onChange={setLayoutPref} />
        </div>
      </header>

      {categories.length > 1 && (
        <div className="mt-6 flex flex-wrap gap-2" role="group" aria-label={t.category}>
          <CategoryChip active={category === null} onClick={() => setCategory(null)} label={t.allCategories} />
          {categories.map(c => (
            <CategoryChip key={c.id} active={category === c.id} onClick={() => setCategory(category === c.id ? null : c.id)} label={c.name} />
          ))}
        </div>
      )}

      {isLoading && <p className="mt-12 text-base text-slate-400">{t.loading}</p>}
      {isError && (
        <p className="mt-12 text-base text-rose-500">
          {t.libraryError}
        </p>
      )}

      {filtered.length > 0 && (
        <ul
          className={
            layout === 'top'
              ? 'mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
              : 'mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'
          }
        >
          {filtered.map(scam => {
            const slug = scam.slug ?? scam.id
            return (
              <li key={slug}>
                {layout === 'top' ? (
                  <ScamCardTop scam={scam} slug={slug} />
                ) : (
                  <ScamCardSide scam={scam} slug={slug} />
                )}
              </li>
            )
          })}
        </ul>
      )}

{data && filtered.length === 0 && (q || category) && (
        <p className="mt-12 text-base text-slate-400">
          {q ? t.libraryNoMatch(q) : t.libraryNoMatchCategory}{' '}
          <button
            onClick={() => { setQ(''); setCategory(null) }}
            className="font-semibold text-violet-600 hover:text-violet-700"
          >
            {t.clearSearch}
          </button>
        </p>
      )}
    </div>
  )
}

function CategoryChip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`cursor-pointer rounded-full border px-3.5 py-1.5 text-base font-medium transition ${
        active
          ? 'border-violet-600 bg-violet-600 text-white'
          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900'
      }`}
    >
      {label}
    </button>
  )
}

/* ── Layout toggle (segmented control) ─────────────────────────── */

function LayoutToggle({
  mode,
  onChange,
}: {
  mode: LayoutMode
  onChange: (next: LayoutMode) => void
}) {
  const t = useUi()
  return (
    <div
      role="radiogroup"
      aria-label={t.libraryLayoutAria}
      className="flex shrink-0 rounded-xl border border-slate-200 bg-white p-1"
    >
      <ToggleButton
        active={mode === 'side'}
        onClick={() => onChange('side')}
        label={t.librarySideLayout}
      >
        <Rows3 className="h-4 w-4" />
      </ToggleButton>
      <ToggleButton
        active={mode === 'top'}
        onClick={() => onChange('top')}
        label={t.libraryTopLayout}
      >
        <LayoutGrid className="h-4 w-4" />
      </ToggleButton>
    </div>
  )
}

function ToggleButton({
  active,
  onClick,
  label,
  children,
}: {
  active: boolean
  onClick: () => void
  label: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      aria-label={label}
      title={label}
      onClick={onClick}
      className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
        active
          ? 'bg-violet-600 text-white'
          : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
      }`}
    >
      {children}
    </button>
  )
}

/* ── Card variants ─────────────────────────────────────────────── */


function ScamCardTop({ scam, slug }: { scam: Scam; slug: string }) {
  const t = useUi()
  const Icon = scamIcon(scam.icon)
  return (
    <Link
      to="/scam-library/$slug"
      params={{ slug }}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all hover:-translate-y-0.5 hover:border-violet-200 hover:shadow-[0_12px_30px_-18px_rgba(106,92,255,0.4)]"
    >
      {scam.image ? (
        <div className="aspect-square w-full overflow-hidden bg-gradient-to-br from-violet-100 to-violet-50">
          <img
            src={scam.image}
            alt=""
            loading="lazy"
            className="scam-illustration h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
          />
        </div>
      ) : (
        <div className="scam-tile flex aspect-square w-full items-center justify-center bg-gradient-to-br from-[#1d1145] via-[#251a5a] to-[#3b2780]">
          <Icon className="h-12 w-12 text-white/85" strokeWidth={1.5} />
        </div>
      )}

      <div className="flex flex-1 flex-col p-4">
        {scam.scamCategory?.name && (
          <p className="text-sm font-semibold text-slate-500">
            {scam.scamCategory.name}
          </p>
        )}
        <h2 className="mt-0.5 text-base font-semibold leading-snug text-slate-900 group-hover:text-violet-700">
          {scam.title}
        </h2>
        {scam.description && (
          <p className="mt-2 line-clamp-3 overflow-hidden text-base leading-snug text-slate-500">
            {scam.description}
          </p>
        )}
        <span className="mt-auto inline-flex items-center gap-1.5 pt-3 text-base font-semibold text-violet-600">
          {t.learnMore}
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  )
}
